import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from '@app/core/logging/logger.service';

/**
 * ¿Es el error de "el almacenamiento está lleno"? Cada navegador lo nombra a su
 * manera: `QuotaExceededError` (código 22) en Chrome/Safari/Edge,
 * `NS_ERROR_DOM_QUOTA_REACHED` (código 1014) en Firefox.
 */
export function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || error.code === 22
    || error.code === 1014;
}

@Injectable({
  providedIn: 'root'
})
export class BrowserStorageService {
  // El logger va por constructor OPCIONAL y no por `inject()` en un campo: hay
  // dobles tipados que extienden esta clase y se construyen con `new` fuera de un
  // contexto de inyección (`chunk-load-recovery.service.spec`). Sin logger no se
  // registra, pero la regla de borrar la clave llena se aplica igual.
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() private readonly logger: LoggerService | null = null,
  ) {}

  // Métodos para localStorage
  setLocalStorageItem(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }
  }

  getLocalStorageItem(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.warn('localStorage not available:', error);
        return null;
      }
    }
    return null;
  }

  removeLocalStorageItem(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }
  }

  clearLocalStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }
  }

  // Métodos para sessionStorage
  /**
   * Escribe en sessionStorage.
   *
   * ⚠️ **Si no cabe, se BORRA la clave** en vez de dejar el valor anterior: un
   * `setItem` que falla por cuota no toca lo que había, así que un lector habría
   * mostrado el dato VIEJO como si fuera el actual (p. ej. el árbol de empresas de
   * `balance_object` antes de un cambio de nivel). Sin la clave, cada lector cae a
   * su estado vacío o recarga, que es honesto. Se registra la clave y el tamaño
   * aproximado, NUNCA el valor (lleva tokens y datos de la sesión cifrados en base64).
   *
   * Cualquier otro error (almacenamiento bloqueado, modo privado) sigue como
   * antes: se registra y no se toca nada.
   */
  setSessionStorageItem(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        if (isQuotaExceededError(error)) {
          this.logger?.warn('[Storage] sessionStorage lleno: se borra la clave para no dejar el valor anterior', {
            key,
            approxBytes: value.length * 2,
          });
          try {
            sessionStorage.removeItem(key);
          } catch {
            // Si ni borrar se puede, no queda nada más que hacer desde aquí.
          }
          return;
        }
        this.logger?.warn('sessionStorage not available:', error);
      }
    }
  }

  getSessionStorageItem(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        this.logger?.warn('sessionStorage not available:', error);
        return null;
      }
    }
    return null;
  }

  /** Comprueba si sessionStorage está disponible (no bloqueado por Tracking Prevention, etc.) */
  isSessionStorageAvailable(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  removeSessionStorageItem(key: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.warn('sessionStorage not available:', error);
      }
    }
  }

  /** Borra de sessionStorage todas las claves que empiezan con `prefijo`. */
  removeSessionStorageItemsWithPrefix(prefijo: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        // Se juntan primero: borrar mientras se recorre por índice se salta claves.
        const claves: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const clave = sessionStorage.key(i);
          if (clave?.startsWith(prefijo)) claves.push(clave);
        }
        claves.forEach(clave => sessionStorage.removeItem(clave));
      } catch (error) {
        this.logger?.warn('sessionStorage not available:', error);
      }
    }
  }

  clearSessionStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.clear();
      } catch (error) {
        console.warn('sessionStorage not available:', error);
      }
    }
  }

  // Método para verificar si estamos en el navegador
  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // Método seguro para obtener elementos del DOM
  getElementById(id: string): HTMLElement | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return document.getElementById(id);
      } catch (error) {
        console.warn('document not available:', error);
        return null;
      }
    }
    return null;
  }
}
